import { useEffect, useRef, useState } from "react";
import { classifySdsNeed } from "@/lib/manufacturing/materialsMsdsClient";
import type { SdsClassificationResult } from "@/lib/manufacturing/msdsClassify";

const DEBOUNCE_MS = 600;

type Args = {
  name: string;
  category: string;
  unit: string;
  vendor: string;
  /** When true, skip network (e.g. empty name/category). */
  enabled?: boolean;
};

/**
 * MSDS-2 — debounced SDS-need classification while the material modal is open.
 * Does not auto-write radios; the triage strip applies suggestion on demand.
 */
export function useMsdsClassifySuggestion(args: Args) {
  const { name, category, unit, vendor, enabled = true } = args;
  const [classification, setClassification] = useState<SdsClassificationResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const requestIdRef = useRef(0);

  useEffect(() => {
    const nameTrim = name.trim();
    const categoryTrim = category.trim();
    if (!enabled || !nameTrim || !categoryTrim) {
      setClassification(null);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);
    const timer = window.setTimeout(() => {
      const requestId = ++requestIdRef.current;
      void (async () => {
        const result = await classifySdsNeed({
          name: nameTrim,
          category: categoryTrim,
          unit,
          vendor,
        });
        if (requestId !== requestIdRef.current) return;
        if (!result.ok) {
          setClassification(null);
          setError(result.error || "Classification failed");
          setLoading(false);
          return;
        }
        setClassification(result.data.classification);
        setError(null);
        setLoading(false);
      })();
    }, DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timer);
    };
  }, [name, category, unit, vendor, enabled]);

  return { classification, loading, error };
}
